import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { rem } from 'polished'
import styled from 'styled-components'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import {
  formatDate,
  getOccupancyDescription,
} from 'components/SearchV2/Utils/GlobalSearchV2'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/DateFloatingPanel'
import LocationFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/LocationFloatingPanel'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'
import useStepsController from 'components/SearchV2/Hooks/useStepsController'
import { DATE_SEARCH_OPTION_IDS, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { connect } from 'react-redux'
import BusinessTravellerAccountGuard from 'businessTraveller/components/BusinessTravellerAccountGuard'
import BusinessTravellerSelectHotelsMobile from 'businessTraveller/components/select-traveller/BusinessTravellerSelectHotelsMobile'
import { BusinessTravellerMultiRoomHotelsSelectMobile, BusinessTravellerMultiRoomHotelsSelectDisabled } from 'businessTraveller/components/select-traveller/multi-room-hotels'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import BusinessTravellerSelectLoadingSkeleton from 'businessTraveller/components/select-traveller/BusinessTravellerSelectLoadingSkeleton'
import useRecentSearches from 'hooks/Search/useRecentSearches'
import { EmptyArray } from 'lib/array/arrayUtils'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    &:not(:first-child) {
      margin-top: ${rem(8)};
    }
  }
`

export enum searchSteps {
  LOCATION = 'location',
  DATE = 'date',
  GUESTS = 'guests',
  MULTI_ROOMS = 'multiRooms',
}

interface Props {
  closeSearch?: () => void
  onSearch: () => void
  isInPage?: boolean; // i.e. not part of drawer
}

interface MappedStateProps {
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
}

function HotelsTabContent(props: Props & MappedStateProps) {
  const { onSearch, travellerEmployees } = props

  const isLebtMultiRoomHotelEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtMultiRoomHotel)

  const {
    searchItem,
    checkinDate,
    checkoutDate,
    occupancies = EmptyArray,
    areRoomErrorsShown,
    flexibleMonths,
    flexibleNights,
    dateSearchOptionId,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const { openedSearchVertical, option } = useGlobalSearchURLHashVertical()

  const isDateSelectOpen = openedSearchVertical === 'hotels' && option === 'dateSelect'

  const recentSearches = useRecentSearches()

  const stepsValidation = useMemo(
    () => ({
      [searchSteps.LOCATION]: searchItem,
      [searchSteps.DATE]: checkinDate && checkoutDate,
      [searchSteps.GUESTS]: isLebtMultiRoomHotelEnabled ? occupancies.length > 0 : travellerEmployees.length > 0,
      [searchSteps.MULTI_ROOMS]: isLebtMultiRoomHotelEnabled && occupancies.length > 0,
    }),
    [searchItem, checkinDate, checkoutDate, travellerEmployees, isLebtMultiRoomHotelEnabled, occupancies.length],
  )

  const flexibleSearchSelectionText =
    prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights)

  const [activeStep, setActiveStep] = useState<searchSteps>()
  const { goToNextStep, goToPreviousStep, closeActiveStep, isSearchEnabled } =
    useStepsController({ activeStep, setActiveStep, stepsValidation, onSearch })

  const openLocationSearchStep = useCallback(
    () => setActiveStep(searchSteps.LOCATION),
    [setActiveStep],
  )
  const openDateSearchStep = useCallback(
    () => setActiveStep(searchSteps.DATE),
    [setActiveStep],
  )
  const openGuestsSearchStep = useCallback(
    () => setActiveStep(searchSteps.GUESTS),
    [setActiveStep],
  )

  const openMultiRoomsSearchStep = useCallback(
    () => setActiveStep(searchSteps.MULTI_ROOMS),
    [setActiveStep],
  )

  useEffect(() => {
    if (isDateSelectOpen) {
      openDateSearchStep()
    }
  }, [isDateSelectOpen, openDateSearchStep])

  const { checkin, checkout } = useMemo(
    () => ({
      checkin: formatDate(checkinDate),
      checkout: formatDate(checkoutDate),
    }),
    [checkinDate, checkoutDate],
  )

  const guestsDescription = useMemo(
    () => getOccupancyDescription(occupancies),
    [occupancies],
  )
  const dateSearchProps = useDateSearchProps({
    onAnytimeDatesSelected: closeActiveStep,
  })

  return (
    <ContentWrapper>
      <LocationFloatingPanel
        typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
        isOpen={activeStep === searchSteps.LOCATION}
        onClose={closeActiveStep}
        onConfirm={openDateSearchStep}
        onGoBack={goToPreviousStep}
        typeaheadVertical="hotel"
        recentSearches={recentSearches}
      />

      <DateFloatingPanel
        {...dateSearchProps}
        isOpen={activeStep === searchSteps.DATE}
        onClose={closeActiveStep}
        onConfirm={goToNextStep}
        onGoBack={goToPreviousStep}
        confirmText={isSearchEnabled ? 'Search' : 'Apply'}
      />

      <FieldsWrapper>
        {isLebtMultiRoomHotelEnabled && (
          <BusinessTravellerAccountGuard
            employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
            loading={<BusinessTravellerSelectLoadingSkeleton />}
            fallback={<BusinessTravellerMultiRoomHotelsSelectDisabled />}
          >
            <BusinessTravellerMultiRoomHotelsSelectMobile
              activeStep={activeStep}
              openEmployeeMenu={openGuestsSearchStep}
              openRoomMenu={openMultiRoomsSearchStep}
              openLocationMenu={openLocationSearchStep}
            />
          </BusinessTravellerAccountGuard>
        )}

        <SearchFormField
          label="Where"
          placeholder="Search destination, landmark or hotel"
          value={searchItem?.format.mainText}
          onClick={openLocationSearchStep}
        />

        <SearchFormFieldGroup>
          {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <SearchFormField
              label="When"
              onClick={openDateSearchStep}
              placeholder="Anytime"
              value={flexibleSearchSelectionText}
            />
          )}
          {dateSearchOptionId !== DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <>
              <SearchFormField
                label="Check in"
                placeholder="Select date"
                value={checkin}
                onClick={openDateSearchStep}
              />
              <SearchFormField
                label="Check out"
                placeholder="Select date"
                value={checkout}
                onClick={openDateSearchStep}
              />
            </>
          )}
        </SearchFormFieldGroup>
        <BusinessTravellerAccountGuard
          employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
          loading={<BusinessTravellerSelectLoadingSkeleton />}
          fallback={<SearchFormField
            label="Traveller"
            value={guestsDescription}
            onClick={openGuestsSearchStep}
            hasError={areRoomErrorsShown}
            disabled
          />}
        >
          <BusinessTravellerSelectHotelsMobile
            activeStep={activeStep}
            onBusinessTravellerSelectClick={openGuestsSearchStep}
            gotoNextStep={goToNextStep}
          />
        </BusinessTravellerAccountGuard>
      </FieldsWrapper>
    </ContentWrapper>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState) => {
  return {
    travellerEmployees: selectSelectedTravellerEmployees(appState),
  }
})(HotelsTabContent)
